import { useState, useCallback, useMemo, useEffect } from 'preact/hooks';
import { useFilter } from '../../contexts/FilterContext';
import { useUnitCoverage } from '../../hooks/useUnitCoverage';
import { extractRiskCityName } from '../../functions/geospatial';
import {
  buildFilterOptionsFromRisks,
  buildCityOptionsFromRisks,
  dayIndexToMs,
  publicationBoundsFromRisks,
} from '../../components/geospatial-intelligence-page';
import type { GeospatialMapAreaSelection } from '../../components/geospatial-intelligence-page';
import type { Risk } from '../../types/api';

export function useGeospatialPage() {
  const filter = useFilter();
  const [showAllRisks, setShowAllRisks] = useState(true);

  const unitCoverage = useUnitCoverage({
    filterLoading: filter.loading,
    scopeUserId: filter.selectedClient?.user_id_no ?? null,
    showAllRisks,
  });

  const [toggleAvailable, setToggleAvailable] = useState(false);
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [sourceType, setSourceType] = useState('');
  const [originator, setOriginator] = useState('');
  const [operatingMode, setOperatingMode] = useState('');
  const [riskCategory, setRiskCategory] = useState('');
  const [impactTarget, setImpactTarget] = useState('');
  const [publicationFromIndex, setPublicationFromIndex] = useState(0);
  const [publicationToIndex, setPublicationToIndex] = useState(0);
  const [rawRisks, setRawRisks] = useState<Risk[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [mapSelection, setMapSelection] = useState<GeospatialMapAreaSelection>({
    country: null,
    risks: [],
  });

  const options = useMemo(
    () => buildFilterOptionsFromRisks(rawRisks, country),
    [rawRisks, country]
  );

  const publicationFromDayMs = useMemo(
    () => dayIndexToMs(options.publicationBounds, publicationFromIndex),
    [options.publicationBounds, publicationFromIndex]
  );

  const publicationToDayMs = useMemo(
    () => dayIndexToMs(options.publicationBounds, publicationToIndex),
    [options.publicationBounds, publicationToIndex]
  );

  useEffect(() => {
    const valid = buildCityOptionsFromRisks(rawRisks, country);
    if (city && !valid.includes(city)) setCity('');
  }, [country, rawRisks, city]);

  const onRawRisksLoaded = useCallback((risks: Risk[]) => {
    setRawRisks(risks);
    const bounds = publicationBoundsFromRisks(risks);
    const maxIdx = Math.max(0, bounds.dayCount - 1);
    setPublicationFromIndex(0);
    setPublicationToIndex(maxIdx);
    setCountry((c) => {
      if (!c) return c;
      return risks.some((r) => (r.country || '').trim() === c) ? c : '';
    });
    setCity((ct) => {
      if (!ct) return ct;
      return risks.some((r) => extractRiskCityName(r.location) === ct) ? ct : '';
    });
    setSourceType((s) => {
      if (!s) return s;
      return risks.some((r) => (r.source_type || '').trim() === s) ? s : '';
    });
    setOriginator((o) => {
      if (!o) return o;
      return risks.some((r) => (r.originator || '').trim() === o) ? o : '';
    });
    setOperatingMode((m) => {
      if (!m) return m;
      return risks.some((r) => (r.operating_mode || '').trim() === m) ? m : '';
    });
    setRiskCategory((cat) => {
      if (!cat) return cat;
      return risks.some((r) => (r.risk_category || '').trim() === cat) ? cat : '';
    });
    setImpactTarget((im) => {
      if (!im) return im;
      return risks.some((r) => (r.impact_target || '').trim() === im) ? im : '';
    });
  }, []);

  const onShowAllRisksChange = useCallback((showAll: boolean) => {
    setShowAllRisks(showAll);
    setCountry('');
    setCity('');
    setSourceType('');
    setOriginator('');
    setOperatingMode('');
    setRiskCategory('');
    setImpactTarget('');
  }, []);

  return {
    showAllRisks,
    unitCoverage,
    toggleAvailable,
    country,
    setCountry,
    city,
    setCity,
    sourceType,
    setSourceType,
    originator,
    setOriginator,
    operatingMode,
    setOperatingMode,
    riskCategory,
    setRiskCategory,
    impactTarget,
    setImpactTarget,
    publicationFromIndex,
    setPublicationFromIndex,
    publicationToIndex,
    setPublicationToIndex,
    publicationFromDayMs,
    publicationToDayMs,
    options,
    filtersOpen,
    setFiltersOpen,
    mapSelection,
    setMapSelection,
    onRawRisksLoaded,
    onShowAllRisksChange,
    setToggleAvailable,
  };
}
